import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import routes from '../config/routes';
import {
  KeyboardArrowDown,
  Message,
  Logout,
  AccountCircle,
  MedicalServices,
  CalendarToday,
  Description,
  Settings,
} from '@mui/icons-material';
import { HeaderLogo } from './virtualpat-theme/VirtualPATLogo';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { useSnackbar } from './SnackbarProvider';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import FacilitySelector from './FacilitySelector';

export default function PatientHeader({ patient }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [unreadMessages, setUnreadMessages] = React.useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSnackbar } = useSnackbar();
  const { mode: themeMode } = useCustomTheme() || { mode: 'light' };
  const { logout: authLogout } = useEnterpriseAuth();
  
  React.useEffect(() => {
    const checkUnreadMessages = () => {
      const messages = JSON.parse(localStorage.getItem('patientMessages') || '[]');
      const unreadCount = messages.filter(m => !m.read).length;
      setUnreadMessages(unreadCount);
    };
    
    checkUnreadMessages();

    const handleStorage = event => {
      if (event.key === 'patientMessages') {
        checkUnreadMessages();
      }
    };

    const handleVisibility = () => {
      if (!document.hidden) {
        checkUnreadMessages();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', checkUnreadMessages);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', checkUnreadMessages);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      // Clear patient-specific data
      localStorage.removeItem('patientToken');
      localStorage.removeItem('patientData');
      localStorage.removeItem('patientMessages');
      
      // Use the EnterpriseAuth logout which handles all auth cleanup
      await authLogout();
      
      showSnackbar('You have been logged out successfully.', 'info');
      navigate(routes.login());
    } catch (error) {
      console.error('[PatientHeader] Logout error:', error);
      // Even if logout fails, clear local data and redirect
      localStorage.clear();
      sessionStorage.clear();
      navigate(routes.login());
    }
  };

  const handleMessages = () => {
    navigate('/patient/messages');
  };

  const displayName = patient?.firstName || patient?.email?.split('@')[0] || 'Patient';

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: themeMode === 'dark' ? 'background.paper' : '#fff',
        color: 'primary.main',
        height: 80,
        justifyContent: 'center',
        mb: 0,
        borderRadius: 0,
        border: 'none',
        boxShadow: '0 2px 8px 0 #919EAB1A',
        fontFamily: 'Inter, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 80,
          position: 'relative',
          px: { xs: 1, sm: 3, md: 6 },
          py: 0,
        }}
      >
        {/* Left: Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <HeaderLogo
            onClick={() => navigate('/patient/dashboard')}
            forceDark={themeMode === 'dark'}
            sx={{
              mr: { xs: 1, sm: 2, md: 2.5 },
              cursor: 'pointer',
            }}
          />
          {!isMobile && (
            <Typography
              variant="h6"
              sx={{
                ml: 2,
                color: 'primary.main',
                fontWeight: 500,
                fontSize: '1.1rem',
              }}
            >
              Patient Portal
            </Typography>
          )}
        </Box>

        {/* Center: Quick Navigation (desktop only) */}
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Facility Selector for multi-facility patients */}
            <FacilitySelector />
            
            <Button
              startIcon={<CalendarToday />}
              onClick={() => navigate('/patient/appointments')}
              sx={{
                textTransform: 'none',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              Appointments
            </Button>
            <Button
              startIcon={<MedicalServices />}
              onClick={() => navigate('/patient/medical-history')}
              sx={{
                textTransform: 'none',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              Medical History
            </Button>
            <Button
              startIcon={<Description />}
              onClick={() => navigate('/patient/documents')}
              sx={{
                textTransform: 'none',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              Documents
            </Button>
          </Box>
        )}

        {/* Right: Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {/* Messages Button - Using Message icon instead of Bell */}
          <IconButton
            color="inherit"
            onClick={handleMessages}
            size="large"
            sx={{ mr: 2 }}
            aria-label="Messages"
          >
            <Badge
              badgeContent={unreadMessages}
              color="error"
              overlap="circular"
              invisible={unreadMessages === 0}
            >
              <Message sx={{ fontSize: 28, color: theme => theme.palette.action.active }} />
            </Badge>
          </IconButton>

          {/* Profile Dropdown */}
          <Button
            color="inherit"
            onClick={handleMenuOpen}
            sx={{
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
              px: 1,
              borderRadius: 20,
              '&:hover': {
                bgcolor: 'action.hover',
              },
              color: 'text.primary',
            }}
            aria-controls={anchorEl ? 'patient-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'secondary.light',
                color: 'secondary.contrastText',
                fontSize: '1rem',
                mr: 1,
              }}
            >
              {displayName[0].toUpperCase()}
            </Avatar>
            {!isMobile && (
              <Typography
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                {displayName}
              </Typography>
            )}
            <KeyboardArrowDown sx={{ ml: 0.5, color: 'text.secondary' }} />
          </Button>

          <Menu
            id="patient-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                minWidth: 200,
              },
            }}
          >
            <MenuItem onClick={() => { navigate('/patient/profile'); handleMenuClose(); }}>
              <AccountCircle sx={{ mr: 2, color: 'text.secondary' }} />
              My Profile
            </MenuItem>
            {!isMobile && (
              <>
                <MenuItem onClick={() => { navigate('/patient/appointments'); handleMenuClose(); }}>
                  <CalendarToday sx={{ mr: 2, color: 'text.secondary' }} />
                  Appointments
                </MenuItem>
                <MenuItem onClick={() => { navigate('/patient/medical-history'); handleMenuClose(); }}>
                  <MedicalServices sx={{ mr: 2, color: 'text.secondary' }} />
                  Medical History
                </MenuItem>
                <MenuItem onClick={() => { navigate('/patient/documents'); handleMenuClose(); }}>
                  <Description sx={{ mr: 2, color: 'text.secondary' }} />
                  Documents
                </MenuItem>
              </>
            )}
            <MenuItem onClick={() => { navigate('/patient/settings'); handleMenuClose(); }}>
              <Settings sx={{ mr: 2, color: 'text.secondary' }} />
              Settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 2, color: 'text.secondary' }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
